<template>
  <div>
    <b-dropdown variant="light" size="sm" :disabled="busy" v-if="showFilter">
      <template #button-content>
        <span :class="!filterType ? 'text-muted' : 'text-primary'">
          <i class="fa fa-fw fa-filter"></i>
          {{ trans(filterType || 'all') }}
        </span>
      </template>
      <b-dropdown-item @click="filterType = null; onRefresh()">
        <i :class="`fa fa-fw ${!filterType ? 'fa-check' : ''}`"></i>
        {{ trans('all') }}
      </b-dropdown-item>
      <b-dropdown-item @click="filterType = 'archived'; onRefresh()">
        <i :class="`fa fa-fw ${filterType == 'archived' ? 'fa-check' : ''} text-danger`"></i>
        <span class="text-danger">{{ trans('archived') }}</span>
      </b-dropdown-item>
    </b-dropdown>
    <b-button v-if="add && !disabled" variant="outline-primary" size="sm" class="float-right mb-2" @click="onClickAdd">
      <i class="fa fa-fw fa-plus"></i> {{ trans('add') }}
    </b-button>
    <b-button variant="secondary" size="sm" @click="fetch" :title="trans('refresh')" :disabled="busy" class="float-right mb-2 mr-2">
      <i class="fa fa-fw fa-redo" v-if="!busy"></i>
      <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
    </b-button>
    <b-table
      :id="`${type}-table`"
      ref="table"
      class="bg-white"
      small
      fixed
      responsive
      show-empty
      :fields="fields"
      :items="items"
      :busy="busy"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>{{ trans('please_wait') }}</strong>
        </div>
      </template>

      <template #empty>
        <p align="center">{{ trans('none') }}</p>
      </template>

      <template #cell(action)="data">
        <div v-if="!disabled" class="text-center">
          <b-dropdown
            variant="link"
            size="sm"
            class="p-0"
            toggle-class="text-muted"
            boundary="window"
            right
            :disabled="busy || disabled"
          >
            <template #button-content>
              <i class="fa fa-fw fa-ellipsis-h"></i>
            </template>
            <b-dropdown-item @click="onClickEdit(data.item)" v-if="!data.item.deleted_at">
              <i class="fa fa-fw fa-edit"></i>
              {{ trans('edit') }}
            </b-dropdown-item>
            <b-dropdown-item @click="onClickRun(data.item)" v-if="!data.item.deleted_at && data.field.hasRun">
              <i class="fa fa-fw fa-play"></i>
              {{ trans('run') }}
            </b-dropdown-item>
            <b-dropdown-item @click="onClickDelete(data.item)" v-if="!data.item.deleted_at">
              <span class="text-danger">
                <i class="fa fa-fw fa-trash"></i>
                {{ trans(deleteType) }}
              </span>
            </b-dropdown-item>
            <b-dropdown-item @click="onClickRestore(data.item)" v-if="!!data.item.deleted_at">
              <span class="text-danger">
                <i class="fa fa-fw fa-trash-restore"></i>
                {{ trans('restore') }}
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <b-modal
      :id="`${type}-dynamic-modal`"
      :title="trans(type)"
      :ok-title="trans('save')"
      :ok-disabled="busy"
      :cancel-title="trans('cancel')"
      @ok="onSave"
    >
      <dynamic-form
        :organization="organization"
        :fields="fields"
        v-model="currentItem"
        :validation="validation"
        ref="dynamicForm"
      ></dynamic-form>
    </b-modal>

    <p v-if="error" class="text-danger">
      {{ error }}
    </p>
  </div>
</template>

<script>
  import DynamicForm from './DynamicForm'

  export default {
    props: {
      organization: Object,
      type: String,
      url: String,
      loadUrl: String,
      fields: {
        type: Array,
        default: [],
      },
      add: {
        type: Boolean,
        default: false,
      },
      showFilter: {
        type: Boolean,
        default: false,
      },
      beforeLoad: Function,
      beforeSave: Function,
      deleteType: {
        type: String,
        default: 'archive', // or delete
      },
      validation: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default :false,
      },
    },
    components: {
      DynamicForm,
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        filterType: null,
        busy: false,
        error: null,
        items: [],
        currentItem: {},
      }
    },
    created() {
      this.fetch();
    },
    methods: {
      onClickEdit(item) {
        this.currentItem = _.cloneDeep(item);
        this.$bvModal.show(`${this.type}-dynamic-modal`);
      },
      onClickRun(item) {
        let url = `${this.url}/${item.id}/run`;
        axios.post(url)
        .then(response => {
          this.$bvToast.toast(response.data.message, {
            title: trans('success'),
            variant: 'success',
            solid: true,
          })
          this.fetch()
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onClickRestore(item) {
        let url = `${this.url}/${item.id}/restore`;
        axios.post(url)
        .then(response => {
          this.$bvToast.toast(response.data.message, {
            title: trans('success'),
            variant: 'success',
            solid: true,
          })
          this.fetch()
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onClickDelete(item) {
        this.$bvModal.msgBoxConfirm(trans(this.deleteType+'_confirm'), {
          title: trans(this.type),
          okTitle: trans(this.deleteType),
          okVariant: 'danger',
          cancelTitle: trans('cancel'),
        }).then(confirmed => {
          if (!confirmed) return
          let url = `${this.url}/${item.id}`;
          axios.delete(url)
          .then(response => {
            this.$bvToast.toast(response.data.message, {
              title: trans('success'),
              variant: 'success',
              solid: true,
            })
            this.fetch()
          })
          .catch(error => {
            this.error = _.errorFromAjax(error)
            _.toastError(this, this.error)
            this.fetch()
          })
        })
      },
      onClickAdd(item) {
        this.currentItem = {};
        this.$bvModal.show(`${this.type}-dynamic-modal`);
      },
      onSave(e){
        e.preventDefault()
        // check validation
        if (this.validation && !this.$refs.dynamicForm.$refs.form.reportValidity()) {
            return;
        }

        this.busy = true
        this.error = null

        let url = this.url;
        const method = this.currentItem.id ? 'patch' : 'post'
        if (this.currentItem.id) url += `/${this.currentItem.id}`

        let values = this.currentItem
        if (this.beforeSave) {
          values = this.beforeSave(values)
        }

        return axios[method](url, values)
        .then(response => {
          this.busy = false
          _.toastSuccess(this, trans('saved_with_success'))
          this.fetch()
          this.$nextTick(() => {
              this.$bvModal.hide(`${this.type}-dynamic-modal`)
          })
        })
        .catch(error => {
          this.busy = false
          this.items = []
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
          this.fetch()
        })
      },
      onRefresh() {
        this.fetch()
      },
      fetch(){
        if (!this.url) return
        this.busy = true
        this.error = null

        let url = this.loadUrl ? this.loadUrl : this.url

        if (this.filterType) {
          url += (url.indexOf('?') >= 0 ? '&' : '?') + `filter=${this.filterType}`
        }

        return axios.get(url)
        .then(response => {
          this.busy = false
          if (this.beforeLoad) {
            response.data = this.beforeLoad(response.data)
          }
          this.items = _.cloneDeep(response.data)
        })
        .catch(error => {
          this.busy = false
          this.items = []
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
    },
    mounted() {
      window.dynamicTableAndForm = this
    },
  }
</script>
